import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import {
  Groups,
  GroupRemove,
  TableView,
  CalendarMonth,
  EventAvailable,
} from "@mui/icons-material";
import moment from "moment";
import {
  themes,
  scrollTop,
  StatusType,
  tableRowSize,
  stickyHeader,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import BSDateRangePicker from "pages/MyZone/Status/DateRangePicker";
import {
  operationsDailyPayrunHeader,
  dailyPayrunHeaders,
  dailyPayrunColors,
} from "pages/PayRoll/PayRollHeaders";
import { customGetAPI } from "services/utils/CustomAPI";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { DateView } from "./DateView";
export const DailyPayRun = () => {
  const { domain, userScreenAccess, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  const [loading, setLoading] = useState(false);
  const [showGroupAmount, setShowGroupAmount] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dailyPayrunApi, setDailyPayrunApi] = useState(false);
  const [payscheduleStartDate, setPayScheduleStartDate] = useState("");
  const [payscheduleEndDate, setPayScheduleEndDate] = useState("");
  const [payscheduleName, setPayscheduleName] = useState("");
  const [payscheduleId, setPayscheduleId] = useState(0);
  const [payscheduleDetails, setPayscheduleDetails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dailyPayRunDetails, setDailyPayrunDetails] = useState([]);
  const [dailyPayRunData, setDailyPayRunData] = useState([]);
  const containerRef = useRef(null);
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Payroll Processing",
    "Daily Payrun",
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    const getPaySchedule = async () => {
      const res = await customGetAPI(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/list-payschedules/?company_id=${topbarCompanyId}`,
      );
      if (res.ok) {
        setLoading(true);
        setPayscheduleDetails(res?.responseDetails);
        const firstSchedule = res.responseDetails[0];
        setPayscheduleId(firstSchedule?.id);
        setPayScheduleStartDate(
          moment(firstSchedule?.start_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
        );
        setPayScheduleEndDate(
          moment(firstSchedule?.end_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
        );
        setPayscheduleName(firstSchedule?.name);
        setDailyPayrunApi(true);
      }
    };
    getPaySchedule();
  }, [domain, topbarCompanyId]);

  useEffect(() => {
    const getDailyPayrun = async () => {
      const res = await customGetAPI(
        `${domain}payroll/daily_earning_details/?company_id=${topbarCompanyId}&start_date=${payscheduleStartDate}&end_date=${payscheduleEndDate}&payschedule_id=${payscheduleId}`,
      );
      if (res.ok) {
        setDailyPayRunData(res?.responseDetails);
        setLoading(false);
        setDailyPayrunApi(false);
      }
    };
    if (
      dailyPayrunApi &&
      payscheduleStartDate &&
      payscheduleEndDate &&
      payscheduleId
    ) {
      getDailyPayrun();
    }
  }, [
    dailyPayrunApi,
    domain,
    payscheduleEndDate,
    payscheduleId,
    payscheduleStartDate,
    topbarCompanyId,
  ]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setDailyPayrunDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  useEffect(() => {
    if (topbarCompanyId) {
      setLoading(true);
      setDailyPayrunApi(true);
    }
  }, [topbarCompanyId]);

  const round2 = (num) => Number(num.toFixed(2));

  const sumByKey = (data, key) =>
    round2(
      data?.reduce((sum, item) => {
        return sum + (Number(item?.[key]) || 0);
      }, 0) ?? 0,
    );

  const totalOT = sumByKey(dailyPayRunDetails, "overtime_earning");
  const totalShiftEarning = sumByKey(dailyPayRunDetails, "earning_amount");
  const totalPayable = (totalOT + totalShiftEarning).toFixed(2);

  const cardData = [
    {
      id: 1,
      icon: TableView,
      label: "Regular Pay",
      value: totalShiftEarning.toFixed(2),
      bgColor: "#F0FDFA",
      iconBgColor: "#D1FAF5",
      iconBorderColor: "#0F766E",
      iconColor: "#09BD71",
    },
    {
      id: 2,
      icon: GroupRemove,
      label: "OT Pay",
      value: totalOT.toFixed(2),
      bgColor: "#FFF1F2",
      iconBgColor: "#FFE4E6",
      iconBorderColor: "#DC2626",
      iconColor: "#DC2626",
    },
    {
      id: 3,
      icon: Groups,
      label: "Total",
      value: totalPayable,
      bgColor: "#F5F7FF",
      iconBgColor: "#E0E7FF",
      iconBorderColor: "#1D4ED8",
      iconColor: "#1D4ED8",
    },
    {
      id: 4,
      icon: GroupRemove,
      label: "Cycle StartDate",
      value: moment(payscheduleStartDate).format("DD-MM-YYYY"),
      bgColor: "#FFF1F2",
      iconBgColor: "#FFE4E6",
      iconBorderColor: "#EAAB35",
      iconColor: "#EAAB35",
    },
    {
      id: 5,
      icon: Groups,
      label: "Cycle EndDate",
      value: moment(payscheduleEndDate).format("DD-MM-YYYY"),
      bgColor: "#F5F7FF",
      iconBgColor: "#E0E7FF",
      iconBorderColor: "#7A2DCC",
      iconColor: "#7A2DCC",
    },
  ];

  const handlePaySchedule = (event, data) => {
    if (data) {
      setPayscheduleId(data?.id);
      setPayScheduleStartDate(
        moment(data?.start_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
      );
      setPayScheduleEndDate(
        moment(data?.end_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
      );
      setPayscheduleName(data?.name);
      setDailyPayrunApi(true);
      setLoading(true);
    }
  };

  const handleStartDate = async (startDateValue, endDateValue) => {
    let start_date = moment(
      new Date(startDateValue.split("/").reverse().join("-")),
    ).format("YYYY-MM-DD");
    let end_date = moment(
      new Date(endDateValue.split("/").reverse().join("-")),
    ).format("YYYY-MM-DD");
    setStartDate(start_date);
    setEndDate(end_date);
  };

  return (
    <React.Fragment>
      <Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Left content */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {showGroupAmount ? "Daywise payrun" : "Daily Pay Run"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and finalize employee payments for the day.
            </Typography>
          </Box>
          {/* Right textbox */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              {/* Icon */}
              <Mui.Stack sx={{ paddingTop: !showGroupAmount ? 3 : 0 }}>
                <Mui.Tooltip
                  title={
                    showGroupAmount
                      ? "Change to Daily payrun"
                      : "Change to Daywise payrun"
                  }
                  arrow
                  placement="top"
                >
                  <Mui.IconButton
                    onClick={() => setShowGroupAmount(!showGroupAmount)}
                    sx={{
                      color: "text.secondary",
                      transition: "color 0.2s ease, background-color 0.2s ease",
                      "&:hover": {
                        color: "primary.main", // icon color on hover
                        backgroundColor: "action.hover", // subtle hover background
                      },
                    }}
                  >
                    {showGroupAmount ? <EventAvailable /> : <CalendarMonth />}
                  </Mui.IconButton>
                </Mui.Tooltip>
              </Mui.Stack>
              {/* Label + Autocomplete */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  minWidth: 350,
                  maxWidth: 400,
                }}
              >
                {!showGroupAmount ? (
                  <>
                    <Mui.Typography
                      sx={{
                        whiteSpace: "nowrap",
                        fontWeight: 500,
                      }}
                    >
                      Pay Schedule
                    </Mui.Typography>

                    <CustomAutoComplete
                      dataList={payscheduleDetails.map((obj) => ({
                        ...obj,
                        label: obj.name,
                      }))}
                      selectedValue={payscheduleName}
                      updateDataFunction={handlePaySchedule}
                      placeHolder="Please select the Payschedule"
                      sx={{ minWidth: 300 }}
                    />
                  </>
                ) : (
                  <BSDateRangePicker
                    handleStartDate={handleStartDate}
                    showToday={false}
                    triggerDate={true}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        {!showGroupAmount ? (
          loading ? (
            <Loader />
          ) : (
            <Paper
              sx={{
                width: "100%",
                height: "78vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden", // important
              }}
            >
              <CustomFilter
                title="Daily Pay Run Records"
                filterHeaders={operationsDailyPayrunHeader}
                data={dailyPayRunData}
                filteredData={dailyPayRunDetails}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={check_access}
              />
              <Box>
                <Mui.Box sx={{ flexGrow: 1, overflow: "auto", pt: 0, mb: 2 }}>
                  <Mui.Box
                    sx={{
                      paddingLeft: 3,
                      paddingRight: 3,
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      gap: 4,
                      flexWrap: "wrap", // ⭐ KEY FIX
                      alignItems: "flex-start",
                    }}
                  >
                    {cardData.map((item) => {
                      const IconComponent = item.icon;
                      return (
                        <Mui.Card
                          key={item.id}
                          sx={{
                            flex: "0 0 200px",
                            width: 200,
                            height: 123,
                            minWidth: 200, // optional safety
                            minHeight: 123,
                            bgcolor: themes?.whiteColor,
                            borderRadius: 1,
                            boxShadow: "0px 4px 4px rgba(0,0,0,0.25)",
                            border: `0.5px solid ${item.iconBorderColor}`,
                          }}
                        >
                          <Mui.CardContent
                            sx={{
                              p: 2,
                              display: "flex",
                              flexDirection: "column",
                              height: "100%",
                            }}
                          >
                            <Mui.Box
                              sx={{
                                width: 35,
                                height: 35,
                                bgcolor: item.iconBgColor,
                                border: `0.5px solid ${item.iconBorderColor}`,
                                borderRadius: 1,
                                boxShadow: "0px 1px 2px rgba(0,0,0,0.25)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 1.5,
                              }}
                            >
                              <IconComponent
                                width={16}
                                height={16}
                                style={{ color: item.iconColor, padding: 2 }}
                              />
                            </Mui.Box>
                            <Mui.Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                mb: "auto",
                              }}
                            >
                              {item.label}
                            </Mui.Typography>
                            <Mui.Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "#32325D",
                              }}
                            >
                              {item.value}
                            </Mui.Typography>
                          </Mui.CardContent>
                        </Mui.Card>
                      );
                    })}
                  </Mui.Box>
                </Mui.Box>
              </Box>
              <Mui.Grid
                sx={{
                  ml: 2,
                  mr: 2,
                  flex: 1, // ⭐ take remaining height
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Mui.TableContainer
                  ref={containerRef}
                  sx={{
                    flex: 1,
                    overflow: "auto",
                  }}
                >
                  <Mui.Table className="border">
                    <Mui.TableHead sx={stickyHeader}>
                      <Mui.TableRow sx={tableRowSize}>
                        {dailyPayrunHeaders.map((header) => (
                          <Mui.TableCell
                            key={header.name}
                            align="center"
                            colSpan={
                              header.children ? header.children.length : 1
                            }
                            rowSpan={header.children ? 1 : 2}
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                      <Mui.TableRow sx={tableRowSize}>
                        {dailyPayrunHeaders
                          .filter((header) => header.children)
                          .flatMap((header) =>
                            header.children.map((child) => (
                              <Mui.TableCell
                                key={child.key}
                                align="center"
                                sx={{
                                  color: themes.headLine,
                                  fontWeight: "bold",
                                  border: "1px solid #ddd",
                                }}
                              >
                                {child.name}
                              </Mui.TableCell>
                            )),
                          )}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {dailyPayRunDetails?.length === 0 ? (
                        <InfoMessage dataLength={dailyPayRunData} />
                      ) : (
                        dailyPayRunDetails
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((row) => (
                            <Mui.TableRow sx={tableRowSize}>
                              {dailyPayrunHeaders.flatMap((header) =>
                                header.children
                                  ? header.children.map((child) => (
                                      <Mui.TableCell
                                        key={child.key}
                                        align="center"
                                        sx={{ border: "1px solid #ddd" }}
                                      >
                                        {row[child.key] ?? "-"}
                                      </Mui.TableCell>
                                    ))
                                  : [
                                      <Mui.TableCell
                                        key={header.key}
                                        align="center"
                                        sx={{ border: "1px solid #ddd" }}
                                      >
                                        {header.key === "day_type" ? (
                                          <span
                                            style={StatusType(
                                              row.day_type,
                                              dailyPayrunColors,
                                            )}
                                          >
                                            {row.day_type}
                                          </span>
                                        ) : (
                                          (row[header.key] ?? "-")
                                        )}
                                      </Mui.TableCell>,
                                    ],
                              )}
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                  </Mui.Table>
                </Mui.TableContainer>
                <Box sx={{ mt: 1 }}>
                  {dailyPayRunDetails?.length > 25 && (
                    <Mui.TablePagination
                      className="custom-pagination"
                      component="div"
                      rowsPerPageOptions={[25, 50, 75, 100]}
                      count={dailyPayRunDetails?.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </Box>
              </Mui.Grid>
            </Paper>
          )
        ) : (
          <DateView startDate={startDate} endDate={endDate} />
        )}
      </Box>
    </React.Fragment>
  );
};
